import { PaperAirplaneIcon, PhoneIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { zodResolver } from "@hookform/resolvers/zod";
import { Dialog } from "@material-tailwind/react";
import { AnimatePresence, motion } from "framer-motion";
import React from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import { socket } from "utils/socket";
import { z } from "zod";
import { EASING } from "../../utils/easingUtils";

interface ContactModalProps {
  open: boolean;
  handleClose: () => void;
  email?: string;
  phone?: string;
  senderId?: any;
  receiverId?: string;
  alertText?: string;
  navigateTo?: string;
  showOwnerPhoneNumber: boolean;
  ownerPhoneNumber?: string;
  ownerEmail?: string;
  ownerFullName?: string;
  locationName?: string;
}

const formSchema = z.object({
  description: z.string().min(10, {
    message: "La description doit contenir au minimum 10 caractères.",
  }),
});

const ContactModal: React.FC<ContactModalProps> = ({
  open,
  handleClose,
  phone,
  alertText,
  navigateTo,
  senderId,
  receiverId,
  showOwnerPhoneNumber,
  ownerPhoneNumber,
  ownerEmail,
  ownerFullName,
  locationName,
}) => {
  const handleCloseModal = () => {
    handleClose();
    if (navigateTo) {
      window.location.href = navigateTo;
    }
  };

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { description: "" },
    mode: "onSubmit",
  });

  const onSubmit: SubmitHandler<z.infer<typeof formSchema>> = async (data) => {
    if (!senderId || !receiverId) {
      toast.error("Informations manquantes pour envoyer le message.");
      return;
    }

    socket.emit(
      "send_messages",
      {
        senderId,
        receiverId,
        content: `Téléphone: ${phone}\n${data.description}`,
        ownerEmail: ownerEmail,
        locationName: locationName,
        ownerFullName: ownerFullName,
      },
      (response: { success: boolean; message?: string }) => {
        if (response?.success) {
          toast.success("Message envoyé avec succès !");
          handleCloseModal();
        } else {
          toast.error("Échec de l'envoi du message, veuillez réessayer.");
        }
      }
    );
  };

  const formatFrenchPhoneNumber = (phone?: string): string => {
    if (!phone) return "Non fourni";
    const digits = phone.replace(/\D/g, "");
    if (!digits.startsWith("33")) {
      return "Numéro invalide";
    }
    const num = digits.slice(2);
    return `+33 ${num[0]}.${num.slice(1, 3)}.${num.slice(3, 5)}.${num.slice(5, 7)}.${num.slice(7, 9)}`;
  };

  return (
    <AnimatePresence>
      {open && (
        <Dialog open={open} handler={handleCloseModal} className="bg-transparent">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.3, ease: EASING.easeOut }}
            className="relative w-full p-6 mx-auto bg-white shadow-2xl rounded-2xl"
          >
            {/* Close Button */}
            <button
              onClick={handleCloseModal}
              className="absolute text-gray-500 transition-colors top-4 right-4 hover:text-gray-700"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>

            {/* Header */}
            <motion.h2
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.1, duration: 0.3 }}
              className="mb-4 text-2xl font-semibold text-center text-gray-800"
            >
              {alertText || "Envoyer un message"}
            </motion.h2>

            <form onSubmit={handleSubmit(onSubmit)}>
              {/* Phone Number */}
              <motion.div
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2, duration: 0.3 }}
                className="flex items-center gap-3 mb-6 text-gray-600"
              >
                <PhoneIcon className="w-5 h-5" />
                <p>
                  Numéro de téléphone : <span className="font-medium">{formatFrenchPhoneNumber(phone)}</span>
                </p>
              </motion.div>

              {showOwnerPhoneNumber === true && (
                <motion.div
                  initial={{ y: -20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.2, duration: 0.3 }}
                  className="flex items-center gap-3 mb-6 text-gray-600"
                >
                  <PhoneIcon className="w-5 h-5" />
                  <p>
                    Numéro de téléphone de l'hôte:{" "}
                    <span className="font-medium">{formatFrenchPhoneNumber(ownerPhoneNumber)}</span>
                  </p>
                </motion.div>
              )}

              {/* Textarea */}
              <motion.div
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.3 }}
                className="mb-6"
              >
                <label className="block mb-2 text-sm font-medium text-gray-700">Votre message</label>
                <textarea
                  {...register("description")}
                  rows={5}
                  className="w-full p-3 transition-colors border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Écrivez votre message ici..."
                />
                {errors.description && (
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="mt-1 text-sm text-red-500"
                  >
                    {errors.description.message}
                  </motion.p>
                )}
              </motion.div>

              {/* Buttons */}
              <motion.div
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.4, duration: 0.3 }}
                className="flex justify-end gap-4"
              >
                <ButtonSecondary
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 font-medium text-gray-600 transition-colors hover:text-gray-800"
                >
                  Annuler
                </ButtonSecondary>
                <ButtonPrimary
                  type="submit"
                  disabled={isSubmitting}
                  className="flex items-center gap-2 px-4 py-2 font-medium text-white transition-colors bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed"
                >
                  <PaperAirplaneIcon className="w-5 h-5" />
                  {isSubmitting ? "Envoi..." : "Envoyer"}
                </ButtonPrimary>
              </motion.div>
            </form>
          </motion.div>
        </Dialog>
      )}
    </AnimatePresence>
  );
};

export default ContactModal;
